import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import MarketingHeader from "@/components/MarketingHeader";

function formatErr(detail) {
  if (!detail) return "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Register() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const { setUser, refreshBilling } = useAuth();
  const [params] = useSearchParams();
  const plan = params.get("plan"); // monthly | yearly | null

  // Funnel hand-off: a visitor who clicked "Create account to download PDF"
  // on the audit result page parks the audit id in localStorage right before
  // bouncing here. We read it on mount, forward it in the register payload
  // (the backend re-links the audit), then route back to the result page
  // with ?pdf=1 so the download fires automatically.
  const [pendingAuditId, setPendingAuditId] = useState(null);
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem("pending_audit_claim");
      if (raw) setPendingAuditId(raw);
    } catch {
      // localStorage disabled — ignore; the user just won't get the PDF flow.
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      return toast.error("Password must be at least 8 characters");
    }
    setBusy(true);
    try {
      const body = { email, password, name };
      if (pendingAuditId) {
        body.claim_audit_id = pendingAuditId;
      }
      const r = await api.post("/auth/register", body);
      setUser(r.data.user);
      const bs = await refreshBilling();
      toast.success(`Welcome, ${r.data.user.name}`);

      // Funnel completion: send the user back to their audit so the PDF
      // download auto-fires. Clear the localStorage marker first so a stale
      // value can't poison a future signup.
      if (pendingAuditId) {
        try {
          window.localStorage.removeItem("pending_audit_claim");
        } catch {
          // ignore
        }
        navigate(`/audit/result/${pendingAuditId}?pdf=1`, { replace: true });
        return;
      }

      // Lifetime accounts skip checkout entirely.
      if (bs?.access_reason === "lifetime") {
        navigate("/dashboard", { replace: true });
        return;
      }
      // ALWAYS land on the Paywall so the user can re-confirm or switch
      // plans before being handed off to Stripe.
      const next = plan ? `/paywall?plan=${plan}` : "/paywall";
      navigate(next, { replace: true });
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      <MarketingHeader showSubscribe={false} />
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="text-xs uppercase tracking-[0.25em] text-neutral-500 font-mono">
            Create account
          </div>
          <h2 className="font-display text-3xl font-semibold mt-3 tracking-tight">
            Start shipping replies.
          </h2>
          {plan && (
            <div
              className="mt-4 text-[11px] font-mono uppercase tracking-[0.18em] text-amber-300 inline-flex items-center gap-2 border border-amber-400/30 bg-amber-400/10 rounded-full px-3 py-1.5"
              data-testid="register-plan-banner"
            >
              <span>Plan pre-selected:</span>
              <span className="text-amber-200">{plan}</span>
              <span className="text-amber-300/70">(you can switch next)</span>
            </div>
          )}
          <form onSubmit={submit} className="mt-6 space-y-3">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Full name"
              data-testid="register-name-input"
              required
              className="w-full bg-[#0d0d0d] border border-[#27272a] rounded-md px-3 py-2.5 text-sm"
            />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              data-testid="register-email-input"
              required
              className="w-full bg-[#0d0d0d] border border-[#27272a] rounded-md px-3 py-2.5 text-sm"
            />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password (8+ chars)"
              data-testid="register-password-input"
              required
              minLength={8}
              className="w-full bg-[#0d0d0d] border border-[#27272a] rounded-md px-3 py-2.5 text-sm"
            />
            <button
              type="submit"
              disabled={busy}
              data-testid="register-submit-button"
              className="w-full flex items-center justify-center gap-2 bg-white text-black hover:bg-neutral-200 transition-colors py-3 rounded-md font-medium text-sm disabled:opacity-50"
            >
              {busy ? (
                <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.8} />
              ) : (
                <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
              )}
              Create account
            </button>
          </form>
          <p className="text-xs text-neutral-500 mt-5">
            Already have an account?{" "}
            <Link
              to="/login"
              data-testid="link-login"
              className="text-white hover:underline"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
